import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
  signal,
  viewChild
} from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterModule,
  RouterOutlet
} from '@angular/router';
import { LocaleOption } from '@entities/domains/header.entity';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageSwitchService } from '@shared/services/switch-language.service';
import { GtmService } from '@wec-shared/services.gtm';
import { GetFooterUseCase } from '@usecases/get.footer.usecase';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs';
import { HeaderStateService } from '@services/header/header.service';
import { LoginIframeComponent } from '@shared/components/login-angular/login-iframe.component';
import { SkeletonBar } from '@shared/components/skeleton-loader/skeleton-loader.model';
import { OrganismHeaderIntro } from '@wec-shared/organism.header-intro';
import { OrganismHeaderMain } from '@wec-shared/organism.header-main';
import { OrganismFooter, FooterModel } from '@wec-shared/organism.footer';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  imports: [
    TranslateModule,
    RouterOutlet,
    RouterModule,
    OrganismHeaderIntro,
    OrganismHeaderMain,
    LoginIframeComponent,
    OrganismFooter
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  currentLang = signal<string>('id');
  dataFooter = signal<FooterModel | null>(null);
  barsIntro = input<SkeletonBar[]>([
    { width: '20%', widthMobile: '100%' },
    { width: '10%', widthMobile: '100%' }
  ]);
  barsMain = input<SkeletonBar[]>([
    { width: '10%', widthMobile: '100%' },
    { width: '50%', widthMobile: '100%' },
    { width: '20%', widthMobile: '100%' }
  ]);

  hideLoginHeader = input<boolean>(false);

  private readonly headerService = inject(HeaderStateService);
  private readonly languageSwitchService = inject(LanguageSwitchService);
  private readonly getFooterUseCase = inject(GetFooterUseCase);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);

  private readonly defaultFlagIcons: Record<string, string> = {
    ID: 'assets/images/header/ID.svg',
    EN: 'assets/images/header/EN.svg'
  };
  private getFlagIcon(
    languages: { icon: string; lang_code: string }[],
    langCode: string
  ): string {
    const found = languages.find((l) => l.lang_code === langCode);
    return found?.icon ?? this.defaultFlagIcons[langCode];
  }

  readonly locales = computed<LocaleOption[]>(() => {
    const languages = this.headerData()?.language ?? [];

    return [
      { code: 'id', label: 'ID', flagIcon: this.getFlagIcon(languages, 'ID') },
      { code: 'en', label: 'EN', flagIcon: this.getFlagIcon(languages, 'EN') }
    ];
  });

  readonly activeLocale = signal<string>(
    typeof localStorage !== 'undefined'
      ? (localStorage.getItem(LanguageSwitchService.storageKey) ??
          LanguageSwitchService.defaultLanguage)
      : LanguageSwitchService.defaultLanguage
  );

  readonly headerData = this.headerService.headerData;

  private readonly loginIframe = viewChild<LoginIframeComponent>('loginIframe');

  constructor(
    public translate: TranslateService,
    private readonly gtmService: GtmService
  ) {
  }

  /**
   * Builds the header nav links with labels resolved for the active language.
   * Re-invoked on every language change (see ngOnInit) so the labels never
   * freeze in the previous language.
   */

  async ngOnInit(): Promise<void> {
    this.gtmService.init(environment.gtmKey);
    this.headerService.init();
    this.onLanguageChange(this.activeLocale());
    // Re-resolve nav link labels whenever the active language changes.
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.getFooter();
      });

    this.getFooter();
  }

  gtmObserver() {
    if (isPlatformBrowser(this.platformId)) {
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe({
          next: (event) => {
            let url = event.url;

            if (url.includes('?')) {
              url = url.split('?')[0];
            }

            if (url.startsWith('/')) {
              url = url.replace('/', '');
            }

            this.switchCaseByPageName(url, this.currentLang());
          }
        });
    }
  }

  switchCaseByPageName(pageName: string, lang: any): void {
    switch (pageName) {
      case 'package':
        this.pushGTM(lang === 'ID' ? 'Paket' : 'Package');
        break;
      case 'credit':
        this.pushGTM('Pulsa');
        break;
      case 'bill':
        this.pushGTM('Tagihan');
        break;
      default:
        break;
    }
  }

  pushGTM(name: string): void {
    this.gtmService?.trackEvent('button_click', {
      eventCategoryDetail: 'Produk Digital',
      eventActionDetail: 'Click Button',
      eventLabelDetail: `${name} | Produk Digital`
    });
  }

  getFooter() {
    this.getFooterUseCase
      .execute()
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.dataFooter.set(res);
        }
      });
  }
  onLanguageChange(code: string): void {
    this.activeLocale.set(code);
    this.headerService.setLanguage(code);
    this.languageSwitchService.setLanguage(code);
  }

  onLoginClick(): void {
    this.loginIframe()?.open();
  }

  onAccountClick(): void {
    window.location.href = window.location.origin + '/user';
  }

}
